import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { auth, db } from './config/firebase-config.js';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import MusicianDashboard from './pages/dashboard/MusicianDashboard.jsx';
import MusicianOnboarding from './onboarding/MusicianOnboarding.jsx';
import PublicMusicianPage from './pages/public/PublicMusicianPage.jsx';
import AdminLogin from './admin/AdminLogin.jsx';
import AdminDashboard from './admin/AdminDashboard.jsx';
import { RequestASongLogo } from './components/common/RequestASongLogo.jsx';

// ✅ Import verification and auth components
import AuthComponent from './auth/AuthComponent.jsx';
import VerificationDashboard from './dashboard/VerificationDashboard.jsx';

// ✅ Import legal components
import LegalAgreement from './legal/LegalAgreement.jsx';
import PrivacyPolicy from './legal/PrivacyPolicy.jsx';
import TermsAndConditions from './legal/TermsAndConditions.jsx';

// 🔒 SECURE LOGGER - Prevents sensitive data exposure
const SENSITIVE_FIELDS = [
  'phoneNumber', 'mobileNumber', 'email', 'phone', 'mobile', 'realName', 
  'fullName', 'legalName', 'emailAddress', 'address', 'street', 'city', 
  'postalCode', 'zipCode', 'ssn', 'socialSecurityNumber', 'bankAccount',
  'stripeAccountId', 'paymentMethod', 'creditCard', 'accountNumber'
];

const sanitizeData = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  if (Array.isArray(data)) {
    return data.map(item => sanitizeData(item));
  }
  
  const sanitized = {};
  for (const [key, value] of Object.entries(data)) {
    if (SENSITIVE_FIELDS.includes(key)) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'string') {
      // Check for email patterns
      if (value.includes('@') && value.includes('.') && value.indexOf('@') < value.lastIndexOf('.')) {
        sanitized[key] = '[EMAIL_REDACTED]';
      }
      // Check for phone patterns
      else if (/(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/.test(value)) {
        sanitized[key] = '[PHONE_REDACTED]';
      }
      // Check for suspicious number patterns
      else if (/\d{8,}/.test(value.replace(/\s/g, ''))) {
        sanitized[key] = '[NUMBER_REDACTED]';
      }
      else {
        sanitized[key] = value;
      }
    } else if (typeof value === 'object') {
      sanitized[key] = sanitizeData(value);
    } else {
      sanitized[key] = value;
    }
  }
  return sanitized;
};

const secureLogger = {
  log: (...args) => {
    if (process.env.NODE_ENV === 'production') return;
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.log(...sanitizedArgs);
  },
  
  error: (...args) => {
    // Always allow errors, but sanitize them
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.error(...sanitizedArgs);
  },
  
  warn: (...args) => {
    if (process.env.NODE_ENV === 'production') return;
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.warn(...sanitizedArgs);
  },
  
  info: (...args) => {
    if (process.env.NODE_ENV === 'production') return;
    const sanitizedArgs = args.map(arg => sanitizeData(arg));
    console.info(...sanitizedArgs);
  }
};

function AppContent() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // ✅ NEW: User profile and verification state management
  const [userProfile, setUserProfile] = useState(null);
  const [verificationStatus, setVerificationStatus] = useState(null);
  const [checkingProfile, setCheckingProfile] = useState(false);
  
  // ✅ KEEP: Original auth state for backward compatibility (if needed elsewhere)
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLogin, setIsLogin] = useState(true);
  
  const [isAdmin, setIsAdmin] = useState(false);
  const [adminCheckComplete, setAdminCheckComplete] = useState(false);
  const [adminCheckInProgress, setAdminCheckInProgress] = useState(false);
  const location = useLocation();

  // Check if current path is admin-related
  const isAdminPath = location.pathname.startsWith('/admin');
  
  // ✅ CHECK FOR PUBLIC MUSICIAN PAGE FIRST - NO AUTH REQUIRED
  const isPublicMusicianPage = location.pathname.startsWith('/m/');

  // ✅ NEW: Check for legal pages - NO AUTH REQUIRED
  const isLegalPage = ['/legal-agreement', '/privacy', '/terms'].some(path => 
    location.pathname.startsWith(path)
  );

  // ✅ NEW: Check user profile and verification status
  const checkUserProfile = async (currentUser) => {
    if (!currentUser) return { profile: null, verification: null };
    
    setCheckingProfile(true);
    try {
      const functions = getFunctions();
      
      // Get profile data
      let profile = null;
      try {
        const getMusicianPrivateData = httpsCallable(functions, 'getMusicianPrivateData');
        const profileResult = await getMusicianPrivateData();
        
        if (profileResult.data.success) {
          profile = profileResult.data.data;
          secureLogger.log('📋 Profile data loaded - field count:', Object.keys(profile || {}).length);
        }
      } catch (profileError) {
        secureLogger.log('Profile not found - user needs onboarding');
        profile = null;
      }
      
      // Get verification status
      let verification = null;
      try {
        const getUserVerificationStatus = httpsCallable(functions, 'getUserVerificationStatus');
        const verificationResult = await getUserVerificationStatus();
        
        if (verificationResult.data.success) {
          verification = verificationResult.data.verificationStatus;
          secureLogger.log('🔐 Verification status loaded successfully');
        }
      } catch (verificationError) {
        secureLogger.log('Verification status not found - using defaults');
        verification = {
          emailVerified: currentUser.emailVerified || false,
          phoneVerified: false,
          onboardingCompleted: false,
          canReceiveRequests: false
        };
      }
      
      setUserProfile(profile);
      setVerificationStatus(verification);
      
      return { profile, verification };
    } catch (error) {
      secureLogger.error('Error checking user profile:', error);
      return { 
        profile: null, 
        verification: {
          emailVerified: currentUser.emailVerified || false,
          phoneVerified: false,
          onboardingCompleted: false,
          canReceiveRequests: false
        }
      };
    } finally {
      setCheckingProfile(false);
    }
  };

  // ✅ Check for email verification URL parameter
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    console.log('🔍 Checking URL parameters:', window.location.search);
    
    if (urlParams.get('emailVerified') === 'true') {
      console.log('🔧 Email verification detected in URL - syncing status...');
      // Clear the URL parameter
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Call sync function if user is logged in
      if (user) {
        console.log('👤 User is logged in, syncing verification...');
        setTimeout(async () => {
          try {
            const functions = getFunctions();
            const checkEmailVerification = httpsCallable(functions, 'checkEmailVerification');
            console.log('📞 Calling checkEmailVerification function...');
            const result = await checkEmailVerification();
            console.log('✅ Email verification sync result:', result.data);
            // Refresh verification status
            if (user) {
              console.log('🔄 Refreshing user profile...');
              await checkUserProfile(user);
              console.log('✅ User profile refreshed');
            }
          } catch (error) {
            console.error('❌ Failed to sync email verification:', error);
          }
        }, 1000); // Small delay to ensure auth is ready
      } else {
        console.log('⚠️ No user logged in, will sync when user logs in');
      }
    } else {
      console.log('ℹ️ No emailVerified parameter found in URL');
    }
  }, [user]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      secureLogger.log('🔐 Auth state changed:', user ? 'User authenticated' : 'No user');
      setUser(user);
      
      if (user && isAdminPath) {
        // ✅ ADMIN LOGIC - Unchanged
        setAdminCheckInProgress(true);
        setAdminCheckComplete(false);
        setIsAdmin(false);
        
        try {
          secureLogger.log('🔍 Checking admin status for authenticated user');
          const adminDoc = await getDoc(doc(db, 'admins', user.uid));
          
          if (adminDoc.exists()) {
            const adminData = adminDoc.data();
            const isUserAdmin = adminData.isAdmin === true && adminData.status === 'active';
            
            secureLogger.log('📋 Admin document data:', {
              exists: true,
              isAdmin: adminData.isAdmin,
              status: adminData.status,
              finalResult: isUserAdmin
            });
            
            setIsAdmin(isUserAdmin);
            
            if (isUserAdmin) {
              secureLogger.log('✅ ADMIN ACCESS GRANTED');
            } else {
              secureLogger.warn('❌ ADMIN ACCESS DENIED - Invalid admin status');
            }
          } else {
            secureLogger.warn('❌ ADMIN ACCESS DENIED - Not found in admins collection');
            setIsAdmin(false);
          }
        } catch (error) {
          secureLogger.error('💥 Error checking admin status:', error);
          setIsAdmin(false);
        } finally {
          setAdminCheckComplete(true);
          setAdminCheckInProgress(false);
        }
      } else if (user && !isAdminPath) {
        // ✅ NEW: Check profile and verification for regular users
        await checkUserProfile(user);
        setIsAdmin(false);
        setAdminCheckComplete(true);
        setAdminCheckInProgress(false);
      } else {
        // No user
        setIsAdmin(false);
        setAdminCheckComplete(true);
        setAdminCheckInProgress(false);
        setUserProfile(null);
        setVerificationStatus(null);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, [isAdminPath]);

  // ✅ KEEP: Original handleAuth function for backward compatibility
  const handleAuth = async (e) => {
    e.preventDefault();
    try {
      if (isLogin) {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (error) {
      alert(error.message);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setIsAdmin(false);
      setAdminCheckComplete(false);
      setAdminCheckInProgress(false);
      setUserProfile(null);
      setVerificationStatus(null);
      // Redirect to appropriate page after logout
      if (isAdminPath) {
        window.location.href = '/admin';
      }
    } catch (error) {
      secureLogger.error('Logout error:', error);
    }
  };

  const handleAdminLoginSuccess = () => {
    secureLogger.log('🔑 Admin login successful, waiting for auth state update...');
  };

  // ✅ NEW: Determine user flow step
  const getUserFlowStep = () => {
    if (!user || userProfile === undefined || verificationStatus === undefined) {
      return 'loading';
    }
    
    // Check if onboarding is completed
    const hasOnboardingCompleted = userProfile?.onboardingCompleted === true || 
                                  verificationStatus?.onboardingCompleted === true;
    
    if (!hasOnboardingCompleted) {
      return 'onboarding'; // User needs to complete onboarding first
    }
    
    // Onboarding is done - check if they want to do verification
    const hasBasicVerification = verificationStatus?.emailVerified && hasOnboardingCompleted;
    const hasCompleteVerification = hasBasicVerification && verificationStatus?.phoneVerified;
    
    if (hasCompleteVerification) {
      return 'dashboard'; // Fully verified, go to dashboard
    }
    
    // Check URL path to see if they're actively doing verification
    if (location.pathname === '/verification') {
      return 'verification'; // User chose to do verification
    }
    
    return 'dashboard'; // Default to dashboard, they can choose verification later
  };

  const handleOnboardingComplete = async (onboardingData) => {
    secureLogger.log('✅ Onboarding completed, updating user profile...');
    
    // Update local state to reflect onboarding completion
    setUserProfile(prev => ({
      ...prev,
      ...onboardingData,
      onboardingCompleted: true
    }));
    
    setVerificationStatus(prev => ({
      ...prev,
      onboardingCompleted: true,
      phoneVerified: onboardingData.phoneVerified || false,
      canReceiveRequests: (prev?.emailVerified || false) && (onboardingData.phoneVerified || false)
    }));
    
    // Refresh profile data from backend
    await checkUserProfile(user);
  };

  const handleVerificationComplete = async () => {
    secureLogger.log('✅ Verification completed, refreshing status...');
    await checkUserProfile(user);
  };

  // ✅ Enhanced loading conditions
  const shouldShowLoading = loading || 
                           checkingProfile ||
                           (isAdminPath && user && (!adminCheckComplete || adminCheckInProgress));

  if (shouldShowLoading) {
    return (
      <div style={{
        minHeight: '100vh',
        backgroundColor: '#111827',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '24px' }}>
            <RequestASongLogo size={80} showText={false} />
          </div>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #374151',
            borderTop: '4px solid #FF6B35',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }}></div>
          <p>
            {loading && 'Loading...'}
            {checkingProfile && 'Setting up your profile...'}
            {!loading && isAdminPath && adminCheckInProgress && 'Verifying admin credentials...'}
            {!loading && isAdminPath && !adminCheckInProgress && !adminCheckComplete && 'Checking admin access...'}
          </p>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      </div>
    );
  }

  // ✅ ADMIN ROUTES - Unchanged
  if (isAdminPath) {
    secureLogger.log('🚪 Admin path detected. Current state:', {
      userPresent: !!user,
      isAdmin,
      adminCheckComplete,
      adminCheckInProgress
    });

    if (!user) {
      return <AdminLogin onLoginSuccess={handleAdminLoginSuccess} />;
    }

    if (!adminCheckComplete) {
      secureLogger.warn('⚠️ Admin check not complete but not showing loading - this should not happen');
      return <div>Checking admin status...</div>;
    }

    if (user && adminCheckComplete && !isAdmin) {
      secureLogger.error(`🚨 SECURITY ALERT: Unauthorized admin access attempt by user at ${new Date().toISOString()}`);
      
      return (
        <div style={{
          minHeight: '100vh',
          backgroundColor: '#111827',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'white',
          textAlign: 'center',
          padding: '20px'
        }}>
          <div style={{
            backgroundColor: '#7F1D1D',
            border: '8px solid #DC2626',
            borderRadius: '24px',
            padding: '48px',
            maxWidth: '500px'
          }}>
            <div style={{ marginBottom: '24px' }}>
              <RequestASongLogo size={60} showText={false} />
            </div>
            <div style={{ fontSize: '64px', marginBottom: '24px' }}>🚫</div>
            <h2 style={{ color: '#FCA5A5', fontSize: '28px', fontWeight: 'bold', marginBottom: '16px' }}>
              🔒 Access Denied
            </h2>
            <p style={{ color: '#FCA5A5', fontSize: '18px', marginBottom: '16px' }}>
              You don't have admin privileges.
            </p>
            <p style={{ color: '#FCA5A5', fontSize: '14px', marginBottom: '32px' }}>
              This security incident has been logged. Contact the platform administrator if you believe this is an error.
            </p>
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
              <button
                onClick={handleSignOut}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#DC2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Sign Out
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#374151',
                  color: 'white',
                  border: 'none',
                  borderRadius: '12px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Go to Main App
              </button>
            </div>
            <div style={{ marginTop: '20px', fontSize: '12px', color: '#9CA3AF' }}>
              Incident ID: {Date.now()} | User: [Protected]
            </div>
          </div>
        </div>
      );
    }

    if (user && adminCheckComplete && isAdmin) {
      secureLogger.log(`✅ ADMIN DASHBOARD ACCESS: User successfully accessed admin dashboard at ${new Date().toISOString()}`);
      return <AdminDashboard onSignOut={handleSignOut} />;
    }

    secureLogger.error('🚨 CRITICAL: Unexpected admin route state', { userPresent: !!user, isAdmin, adminCheckComplete });
    return <div>Unexpected state - please refresh</div>;
  }

  // ✅ NEW: Smart routing based on user flow step
  const userFlowStep = getUserFlowStep();

  return (
    <Routes>
      {/* ✅ PUBLIC MUSICIAN PAGES - NO AUTHENTICATION REQUIRED */}
      <Route path="/m/:musicianId" element={<PublicMusicianPage />} />
      
      {/* ✅ LEGAL PAGES - NO AUTHENTICATION REQUIRED */}
      <Route path="/legal-agreement" element={<LegalAgreement />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/terms" element={<TermsAndConditions userType="audience" />} />
      <Route path="/terms/musicians" element={<TermsAndConditions userType="musician" />} />
      <Route path="/terms/audience" element={<TermsAndConditions userType="audience" />} />
      
      {/* ✅ NEW: Verification route - accessible anytime */}
      <Route path="/verification" element={
        user ? (
          <VerificationDashboard 
            user={user} 
            userProfile={userProfile}
            verificationStatus={verificationStatus}
            onComplete={handleVerificationComplete} 
          />
        ) : (
          <Navigate to="/" replace />
        )
      } />
      
      {/* Authenticated musician routes */}
      {user ? (
        <>
          {/* ✅ NEW: Smart routing based on flow step */}
          <Route path="/" element={
            userFlowStep === 'loading' ? (
              <div>Loading your profile...</div>
            ) : userFlowStep === 'onboarding' ? (
              <MusicianOnboarding 
                user={user} 
                onComplete={handleOnboardingComplete}
                onSkip={() => {
                  // If they skip, mark onboarding as completed anyway
                  setUserProfile(prev => ({ ...prev, onboardingCompleted: true }));
                  setVerificationStatus(prev => ({ ...prev, onboardingCompleted: true }));
                }}
              />
            ) : userFlowStep === 'verification' ? (
              <VerificationDashboard 
                user={user} 
                userProfile={userProfile}
                verificationStatus={verificationStatus}
                onComplete={handleVerificationComplete} 
              />
            ) : (
              <MusicianDashboard 
                user={user} 
                userProfile={userProfile}
                verificationStatus={verificationStatus}
                onSignOut={handleSignOut} 
              />
            )
          } />
          
          {/* ✅ Dashboard route - always accessible */}
          <Route path="/dashboard" element={
            <MusicianDashboard 
              user={user} 
              userProfile={userProfile}
              verificationStatus={verificationStatus}
              onSignOut={handleSignOut} 
            />
          } />
          
          {/* ✅ Onboarding route - accessible if needed */}
          <Route path="/onboarding" element={
            <MusicianOnboarding 
              user={user} 
              onComplete={handleOnboardingComplete}
              onSkip={() => {
                setUserProfile(prev => ({ ...prev, onboardingCompleted: true }));
                setVerificationStatus(prev => ({ ...prev, onboardingCompleted: true }));
              }}
            />
          } />
          
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      ) : (
        <>
          {/* ✅ AUTH COMPONENT for non-authenticated users */}
          <Route path="/" element={<AuthComponent />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </>
      )}
    </Routes>
  );
}

function App() {
  return (
    <Router>
      <Routes>
        <Route path="/admin/*" element={<AppContent />} />
        <Route path="*" element={<AppContent />} />
      </Routes>
    </Router>
  );
}

export default App;